import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useFilteredNavItems } from '../navigation/useFilteredNavItems'
import { ShellProvider, useShell } from './ShellContext'
import { NavRail } from './NavRail'
import { NavDrawer } from './NavDrawer'
import { MobileDock } from './MobileDock'
import { BottomSheet } from './BottomSheet'
import { CommandPalette, PaletteEntry } from './CommandPalette'
import { UserMenu } from './UserMenu'
import { resolveUserName } from './userName'
import { NavEntry, groupItems } from './NavEntry'
import { useSwipe, clamp } from './useSwipe'
import { useShellMode } from './useShellMode'
import { useFocusReturn, useInert } from './useFocusReturn'
import { UserMenuIcons } from './UserMenuIcons'
import { uebernehmeServerFarbschema, useFarbschema } from '../../theme/farbschema'
import type { NavItem, PaletteAction, ShellUser, UserMenuEntry } from './types'

export interface AppShellProps {
  /** Modulname, z. B. "Admin-Modul". */
  moduleName: string
  moduleSubtitle?: string
  /** Logo (z. B. "/logo.png"). */
  logoSrc?: string
  /** Alle Navigationseinträge; Rollen/Feature-Flags/Einsatz/Pilot-Modus werden hier gefiltert. Gruppen über item.group. */
  navItems: NavItem[]
  /** Pfade, die im Handy-Dock erscheinen (4, max. 5). Sonst Einträge mit dock: true, sonst die ersten 4. */
  dockPaths?: string[]
  /** Kurzbezeichnungen im Dock, z. B. { '/taktische-karte': 'Karte' }. */
  dockLabels?: Record<string, string>
  /** Pilot-Modus für die Filterung (nur Pilot-Modul). */
  pilotMode?: 'PILOT' | 'CO_PILOT'
  /** Fester Seitentitel; sonst aus pageTitles/navItems abgeleitet. */
  title?: React.ReactNode
  /** Zusätzliche Titel je Pfad (exakt oder als Präfix, z. B. { '/einsaetze/': 'Einsatz' }). */
  pageTitles?: Record<string, string>
  /** Einsatzwahl im Header, direkt hinter dem Titel: `<EinsatzWahl />`. Am Handy bleibt sie als kompakter Chip sichtbar. */
  einsatzWahl?: React.ReactNode
  /** Slot im Header direkt hinter dem Titel/der Einsatzwahl (z. B. alter Einsatz-Chip). */
  headerLeft?: React.ReactNode
  /** Navigation ausblenden (Rail, Drawer, Scrim, Dock, Bottom-Sheet), z. B. im Piloten-Modus mit nur zwei Zielen. */
  hideNav?: boolean
  /** Slot im Header vor dem Benutzermenü (z. B. Pilot/Co-Pilot-Umschalter). */
  headerRight?: React.ReactNode
  /** Verbindungsstatus rechts im Header (zwischen headerRight und Suche): `<StatusBar />`. Am Handy nur der Punkt. */
  status?: React.ReactNode
  /** Banner unter dem Header, außerhalb des scrollenden Bereichs (immer sichtbar): `<StaleBanner />`, `<EinsatzHinweis />`. */
  banner?: React.ReactNode
  user: ShellUser
  userMenu: UserMenuEntry[]
  /** Feste Aktionen der Befehlspalette (Seiten registrieren weitere über useShellActions). */
  paletteActions?: PaletteAction[]
  /** Inhalt über der Seite innerhalb des scrollenden Bereichs (z. B. Banner). */
  contentTop?: React.ReactNode
  /** localStorage-Schlüssel für "angeheftet". */
  storageKey?: string
  children?: React.ReactNode
}

function readPinned(key: string) {
  try { return localStorage.getItem(key) === '1' } catch { return false }
}

function isTypingTarget(t: EventTarget | null) {
  const el = t as HTMLElement | null
  if (!el) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

/**
 * Seitentitel im Header. Bleibt weniger als ca. 4 Zeichen Platz (sonst nur „F…“), wird der Titel ganz ausgeblendet
 * (visuell versteckt, für Screenreader bleibt er als h1 erhalten) und kommt zurück, sobald wieder genug Platz ist.
 */
const TITEL_MIN_BREITE = 56
function ShellTitel({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLHeadingElement>(null)
  const [versteckt, setVersteckt] = useState(false)
  useEffect(() => {
    const t = ref.current
    const h = t?.parentElement
    if (!t || !h) return
    const messen = () => {
      const natuerlich = t.scrollWidth
      if (!versteckt) {
        if (t.clientWidth < TITEL_MIN_BREITE && natuerlich > t.clientWidth) setVersteckt(true)
        return
      }
      const cs = getComputedStyle(h)
      const gap = parseFloat(cs.columnGap) || 0
      const andere = Array.from(h.children).filter(c => c !== t) as HTMLElement[]
      const belegt = andere.reduce((sum, c) => sum + c.offsetWidth, 0) + gap * andere.length
      const frei = h.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0) - belegt
      if (frei >= Math.min(natuerlich, TITEL_MIN_BREITE) + 16) setVersteckt(false)
    }
    messen()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(messen)
    ro.observe(h)
    Array.from(h.children).forEach(c => ro.observe(c))
    return () => ro.disconnect()
  }, [versteckt, children])
  return <h1 ref={ref} className={`ui-title${versteckt ? ' ui-title--versteckt' : ''}`}>{children}</h1>
}


/**
 * Gemeinsame App-Hülle aller Module: Rail + Drawer (Tablet/PC), Dock + Bottom-Sheet (Handy),
 * Befehlspalette (Strg+K), Header mit Titel, Slots und Benutzermenü.
 */
export function AppShell(props: AppShellProps) {
  return (
    <ShellProvider>
      <ShellInner {...props} />
    </ShellProvider>
  )
}

function ShellInner({
  moduleName, moduleSubtitle, logoSrc, navItems, dockPaths, dockLabels, pilotMode, hideNav, title, pageTitles,
  einsatzWahl, headerLeft, headerRight, status, banner, user: userProp, userMenu: userMenuProp, paletteActions, contentTop, storageKey = 'pilot-hub-nav-pinned', children,
}: AppShellProps) {
  const { mode, wide } = useShellMode()
  const user = useMemo(() => ({ ...userProp, name: resolveUserName(userProp) }), [userProp])
  // Farbschema: nach dem Login gilt der Serverwert; Umschalter („Hell“/„Dunkel“) als Eintrag im Benutzermenü
  const [farbschema, setFarbschema] = useFarbschema()
  useEffect(() => { uebernehmeServerFarbschema(userProp.id, userProp.farbschema) }, [userProp.id, userProp.farbschema])
  const userMenu: UserMenuEntry[] = useMemo(() => {
    const hell = farbschema === 'hell'
    const eintrag: UserMenuEntry = {
      id: 'farbschema', label: hell ? 'Dunkel' : 'Hell', icon: hell ? UserMenuIcons.moon : UserMenuIcons.sun,
      onSelect: () => setFarbschema(hell ? 'dunkel' : 'hell'),
    }
    const i = userMenuProp.findIndex(e => e.type !== 'heading' && e.type !== 'separator' && e.danger)
    if (i < 0) return [...userMenuProp, eintrag]
    const vor = i > 0 && userMenuProp[i - 1].type === 'separator' ? i - 1 : i
    return [...userMenuProp.slice(0, vor), eintrag, ...userMenuProp.slice(vor)]
  }, [userMenuProp, farbschema, setFarbschema])
  const location = useLocation()
  const navigate = useNavigate()
  const { items, hasMission } = useFilteredNavItems(navItems, pilotMode)
  const { actions: registered, paletteOpen, setPaletteOpen } = useShell()

  const rootRef = useRef<HTMLDivElement>(null)
  const bannerRef = useRef<HTMLDivElement>(null)
  // Höhe der Hinweisleiste über dem Inhalt als --ui-banner-h bereitstellen: Vollbild-Seiten (.ui-fill) ziehen sie ab,
  // sonst ragt z. B. die Karte bei sichtbarem Banner unter das Dock.
  useEffect(() => {
    const root = rootRef.current, el = bannerRef.current
    if (!root) return
    if (!el) { root.style.removeProperty('--ui-banner-h'); return }
    const messen = () => root.style.setProperty('--ui-banner-h', `${el.offsetHeight}px`)
    messen()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(messen)
    ro.observe(el)
    return () => { ro.disconnect(); root.style.removeProperty('--ui-banner-h') }
  }, [!!banner])
  const drawerRef = useRef<HTMLElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const menuBtnRef = useRef<HTMLButtonElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const moreRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState(false)
  const [pinnedRaw, setPinnedRaw] = useState(() => readPinned(storageKey))
  const [sheet, setSheet] = useState(false)
  const phone = mode === 'phone'
  const mitNav = !hideNav
  const pinned = pinnedRaw && wide && !phone && mitNav
  const drawerShown = open || pinned

  // ---- Fortschritt (0..1) als CSS-Variable am Root; Ziehen setzt direkt, ohne Re-Render
  const applyNav = useCallback((o: boolean, p: boolean) => {
    const r = rootRef.current
    if (!r) return
    r.classList.remove('is-dragging')
    r.style.setProperty('--p', o || p ? '1' : '0')
  }, [])
  const applySheet = useCallback((o: boolean) => {
    const r = rootRef.current
    if (!r) return
    r.classList.remove('is-sdragging')
    r.style.setProperty('--sp', o ? '1' : '0')
  }, [])
  useLayoutEffect(() => { applyNav(open, pinned) }, [open, pinned, applyNav])
  useLayoutEffect(() => { applySheet(sheet) }, [sheet, applySheet])

  const commitOpen = (o: boolean) => { setOpen(o); applyNav(o, pinned) }
  const commitSheet = (o: boolean) => { setSheet(o); applySheet(o) }
  const setNavP = (p: number) => { const r = rootRef.current; if (!r) return; r.classList.add('is-dragging'); r.style.setProperty('--p', String(p)) }
  const setSheetP = (p: number) => { const r = rootRef.current; if (!r) return; r.classList.add('is-sdragging'); r.style.setProperty('--sp', String(p)) }
  const drawerW = () => drawerRef.current?.offsetWidth || 248
  const sheetH = () => sheetRef.current?.offsetHeight || 400

  // Moduswechsel: nicht mehr passende Overlays schließen
  useEffect(() => {
    if (phone) { if (open) commitOpen(false) } else if (sheet) commitSheet(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone])

  // Seitenwechsel: Overlays schließen
  useEffect(() => {
    if (open && !pinned) commitOpen(false)
    if (sheet) commitSheet(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const setPinned = (p: boolean) => {
    setPinnedRaw(p)
    try { localStorage.setItem(storageKey, p ? '1' : '0') } catch { /* Speicher blockiert */ }
  }
  const toggleMenu = () => {
    if (pinned) { setPinned(false); commitOpen(false) } else commitOpen(!open)
  }
  const togglePin = () => {
    if (pinned) { setPinned(false); commitOpen(true) } else { setPinned(true); commitOpen(false) }
  }

  // ---- Fokus + inert
  useFocusReturn(open && !pinned, drawerRef as React.RefObject<HTMLElement>, '.ui-drawer-head button')
  useFocusReturn(sheet, sheetRef as React.RefObject<HTMLElement>, '.ui-sheet-body .ui-it[aria-current="page"]')
  useInert(drawerRef as React.RefObject<HTMLElement>, !drawerShown)
  useInert(sheetRef as React.RefObject<HTMLElement>, !sheet)

  // ---- Tastatur: Strg+K Palette, Strg+B / [ Menü, Esc schließt
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey
      const k = e.key.toLowerCase()
      if (mod && k === 'k') { e.preventDefault(); setPaletteOpen(!paletteOpen); return }
      if (paletteOpen) return
      if (mod && k === 'b') { if (!mitNav) return; e.preventDefault(); if (phone) commitSheet(!sheet); else toggleMenu(); return }
      if (e.key === '[' && !mod && !e.altKey && !isTypingTarget(e.target) && !phone && mitNav) { e.preventDefault(); toggleMenu(); return }
      if (e.key === 'Escape') {
        if (sheet) commitSheet(false)
        else if (open && !pinned) commitOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })

  // ---- Gesten
  const railSwipe = useSwipe({
    axis: 'x',
    accept: d => d > 0 && !open && !pinned,
    start: () => setNavP(0),
    move: d => setNavP(clamp(d / drawerW(), 0, 1)),
    end: (d, v, c) => commitOpen(!c && (d / drawerW() > 0.35 || v > 0.5)),
  })
  const closeDrawerOpts = {
    axis: 'x' as const,
    accept: (d: number) => d < 0 && open && !pinned,
    start: () => setNavP(1),
    move: (d: number) => setNavP(clamp(1 + d / drawerW(), 0, 1)),
    end: (d: number, v: number, c: boolean) => commitOpen(c || !(1 + d / drawerW() < 0.65 || v < -0.5)),
  }
  const drawerSwipe = useSwipe(closeDrawerOpts)
  const dockSwipe = useSwipe({
    axis: 'y',
    accept: d => d < 0 && !sheet && phone,
    start: () => setSheetP(0),
    move: d => setSheetP(clamp(-d / sheetH(), 0, 1)),
    end: (d, v, c) => commitSheet(!c && (-d / sheetH() > 0.3 || v < -0.5)),
  })
  const closeSheetOpts = {
    axis: 'y' as const,
    accept: (d: number) => d > 0 && sheet,
    start: () => setSheetP(1),
    move: (d: number) => setSheetP(clamp(1 - d / sheetH(), 0, 1)),
    end: (d: number, v: number, c: boolean) => commitSheet(c || !(d / sheetH() > 0.3 || v > 0.5)),
  }
  const grabSwipe = useSwipe(closeSheetOpts)
  const sscrimSwipe = useSwipe(closeSheetOpts)
  // Abdunklung des Drawers: Wisch nach links schließt, Tipp ebenfalls
  const scrimRef = useSwipe(closeDrawerOpts)

  // ---- Dock-Auswahl
  const dockItems = useMemo(() => {
    let sel: NavItem[]
    if (dockPaths) sel = dockPaths.map(p => items.find(i => i.path === p)).filter((x): x is NavItem => !!x)
    else {
      sel = items.filter(i => i.dock)
      if (!sel.length) sel = items.slice(0, 4)
    }
    return sel.slice(0, 5)
  }, [items, dockPaths])
  const inDock = dockItems.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))

  // ---- Titel
  const resolvedTitle = useMemo(() => {
    if (title !== undefined) return title
    const p = location.pathname
    const merged: Record<string, string> = { '/settings': 'Einstellungen', '/feedback': 'Feedback & Roadmap', ...pageTitles }
    if (merged[p]) return merged[p]
    const prefix = Object.keys(merged).filter(k => k.endsWith('/') && p.startsWith(k)).sort((a, b) => b.length - a.length)[0]
    if (prefix) return merged[prefix]
    const all = [...items, ...navItems]
    const hit = all.filter(n => p === n.path || p.startsWith(n.path + '/')).sort((a, b) => b.path.length - a.path.length)[0]
    return hit?.label || document.title
  }, [title, pageTitles, location.pathname, items, navItems])

  // ---- Befehlspalette
  const pageEntries: PaletteEntry[] = useMemo(() => {
    const nav = items.map(it => ({
      id: `nav:${it.path}`, type: 'page' as const, label: it.label, kind: it.group, icon: it.icon,
      run: () => navigate(it.path),
    }))
    const menu: PaletteEntry[] = []
    for (const e of userMenu) {
      if (e.type === 'heading' || e.type === 'separator' || e.danger || e.hideInPalette) continue
      if (!e.to && !e.href && !e.onSelect) continue
      menu.push({
        id: `um:${e.id}`, type: 'page', label: e.label, kind: 'Konto', icon: e.icon,
        run: () => { if (e.href) window.open(e.href, '_blank', 'noopener'); else if (e.to) navigate(e.to, e.state ? { state: e.state } : undefined); e.onSelect?.() },
      })
    }
    return [...nav, ...menu]
  }, [items, userMenu, navigate])
  const actionEntries: PaletteEntry[] = useMemo(
    () => [...(paletteActions ?? []), ...registered].map(a => ({ id: `act:${a.id}`, type: 'action' as const, label: a.label, keywords: a.keywords, kind: a.group, icon: a.icon, run: a.run })),
    [paletteActions, registered],
  )

  const openPalette = () => { if (sheet) commitSheet(false); setPaletteOpen(true) }
  const sheetGroups = groupItems(items)

  return (
    <div
      ref={rootRef}
      className="ui-shell"
      data-mode={mode}
      data-drawer={open ? 'true' : 'false'}
      data-pinned={pinned ? 'true' : 'false'}
      data-sheet={sheet ? 'true' : 'false'}
      data-nav={mitNav ? undefined : 'aus'}
    >
      <a className="ui-skip" href="#ui-main">Zum Inhalt springen</a>

      {!phone && mitNav && (
        <>
          <NavRail
            items={items}
            hasMission={hasMission}
            expanded={drawerShown}
            onToggleMenu={toggleMenu}
            menuRef={menuBtnRef}
            swipeRef={railSwipe}
            moduleName={moduleName}
            logoSrc={logoSrc}
          />
          <div className="ui-scrim" ref={scrimRef as React.Ref<HTMLDivElement>} onClick={() => commitOpen(false)} aria-hidden="true" />
          <NavDrawer
            items={items}
            hasMission={hasMission}
            moduleName={moduleName}
            moduleSubtitle={moduleSubtitle}
            logoSrc={logoSrc}
            pinned={pinned}
            canPin={wide}
            user={user}
            onClose={() => { if (pinned) { setPinned(false) } commitOpen(false); menuBtnRef.current?.focus() }}
            onTogglePin={togglePin}
            onNavigate={() => { if (!pinned) commitOpen(false) }}
            drawerRef={drawerRef}
            swipeRef={drawerSwipe}
            closeRef={closeBtnRef}
          />
        </>
      )}

      <div className="ui-main">
        <header className="ui-header">
          <ShellTitel>{resolvedTitle}</ShellTitel>
          {einsatzWahl}
          {headerLeft}
          <div className="ui-header-right">
            {headerRight}
            {status}
            {!phone && (
              <button type="button" className="ui-search" onClick={openPalette} aria-label="Suchen (Strg+K)" aria-haspopup="dialog">
                <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="ui-search-label">Suchen…</span>
                {mode === 'desktop' && <kbd>Strg K</kbd>}
              </button>
            )}
            <UserMenu user={user} entries={userMenu} />
          </div>
        </header>
        {banner && <div className="ui-banner-slot" ref={bannerRef}>{banner}</div>}
        <main id="ui-main" className="ui-content scrollbar-thin" tabIndex={-1}>
          {contentTop}
          {children ?? <Outlet />}
        </main>
      </div>

      {phone && mitNav && (
        <>
          <MobileDock
            items={dockItems}
            hasMission={hasMission}
            moreOpen={sheet}
            moreActive={!inDock}
            onMore={() => commitSheet(!sheet)}
            swipeRef={dockSwipe}
            moreRef={moreRef}
            shortLabels={dockLabels}
          />
          <BottomSheet label="Alle Bereiche" open={sheet} onClose={() => commitSheet(false)} sheetRef={sheetRef} grabRef={grabSwipe} scrimRef={sscrimSwipe}>
            <button type="button" className="ui-sheet-search" onClick={openPalette}>
              <Search className="h-[18px] w-[18px]" aria-hidden="true" />
              <span>Seiten und Aktionen suchen…</span>
            </button>
            {sheetGroups.map((g, gi) => (
              <div key={g.name ?? `g${gi}`} className="ui-grp-wrap">
                {g.name && <div className="ui-grp">{g.name}</div>}
                {g.items.map(it => <NavEntry key={it.path} item={it} variant="sheet" hasMission={hasMission} onNavigate={() => commitSheet(false)} />)}
              </div>
            ))}
            <div className="ui-grp-wrap">
              <div className="ui-grp">{user.name}</div>
              {userMenu.map((e, i) => {
                if (e.type === 'heading') return <div key={`h${i}`} className="ui-grp">{e.label}</div>
                if (e.type === 'separator') return <span key={`s${i}`} className="ui-sep" role="separator" />
                const cls = e.danger ? 'ui-it ui-it--sheet ui-it--danger' : 'ui-it ui-it--sheet'
                const inner = (
                  <>
                    <span className="ui-it-ic" aria-hidden="true">{e.icon}</span>
                    <span className="ui-lb">{e.label}</span>
                    {e.badge !== undefined && e.badge !== 0 && <span className="ui-tag ui-tag--accent">{e.badge}</span>}
                  </>
                )
                return e.href
                  ? <a key={e.id} className={cls} href={e.href} target="_blank" rel="noopener" onClick={() => commitSheet(false)}>{inner}</a>
                  : <button key={e.id} type="button" className={cls} onClick={() => { commitSheet(false); if (e.to) navigate(e.to, e.state ? { state: e.state } : undefined); e.onSelect?.() }}>{inner}</button>
              })}
            </div>
          </BottomSheet>
        </>
      )}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} pages={pageEntries} actions={actionEntries} showHints={mode === 'desktop'} />
    </div>
  )
}
